import React from 'react';
import type { Ticket, TicketEvent } from '../../api/supportApi';
import { LIFECYCLE_LABEL } from '../../api/supportApi';

/** Shared rendering of a ticket's history, used by the customer page and the admin inbox. */

const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

export const LifecycleBadge: React.FC<{ value: Ticket['lifecycle'] }> = ({ value }) => {
  const colors: Record<string, { bg: string; fg: string }> = {
    open: { bg: 'rgba(68,76,231,0.12)', fg: 'var(--ds-brand-blue)' },
    in_progress: { bg: 'rgba(247,144,9,0.14)', fg: 'var(--ds-warning)' },
    waiting_on_customer: { bg: 'rgba(2,122,72,0.10)', fg: 'var(--ds-success)' },
    resolved: { bg: 'rgba(2,122,72,0.10)', fg: 'var(--ds-success)' },
    closed: { bg: 'rgba(102,112,133,0.14)', fg: 'var(--ds-text-secondary)' },
  };
  const c = colors[value] || colors.open;
  return <span style={{ fontSize: 11.5, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>{LIFECYCLE_LABEL[value] || value}</span>;
};

export const PriorityBadge: React.FC<{ value: Ticket['priority'] }> = ({ value }) => {
  if (value === 'normal') return null;
  const fg = value === 'urgent' ? 'var(--ds-danger)' : value === 'high' ? 'var(--ds-warning)' : 'var(--ds-text-secondary)';
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 999, border: `1px solid ${fg}`, color: fg, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{value}</span>;
};

const eventLine = (e: TicketEvent, viewer: 'customer' | 'admin') => {
  const who = e.actor_type === 'admin' ? (viewer === 'admin' ? e.actor_email : 'Support') : e.actor_type === 'user' ? (viewer === 'customer' ? 'You' : e.actor_email || 'Customer') : 'System';
  switch (e.kind) {
    case 'created': return `${who} opened the ticket`;
    case 'customer_reply': return `${who} replied`;
    case 'admin_reply': return `${who} replied`;
    case 'status': return `Status ${e.body?.replace('→', '→')}`;
    case 'assign': return viewer === 'admin' ? `Assigned to ${e.body}` : 'Assigned to a support agent';
    case 'priority': return `Priority ${e.body}`;
    default: return e.kind;
  }
};

export const TicketHistory: React.FC<{ ticket: Ticket; viewer: 'customer' | 'admin' }> = ({ ticket, viewer }) => {
  const events = ticket.history || [];
  return (
    <ol style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {events.map((e) => {
        const isMessage = e.kind === 'created' || e.kind === 'customer_reply' || e.kind === 'admin_reply';
        const mine = viewer === 'customer' ? e.actor_type === 'user' : e.actor_type === 'admin';
        return (
          <li key={e.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMessage ? (mine ? 'flex-end' : 'flex-start') : 'center' }}>
            {isMessage ? (
              <div style={{ maxWidth: '85%', padding: '8px 12px', borderRadius: 12, background: mine ? 'rgba(68,76,231,0.10)' : 'var(--ds-surface-2, var(--ds-surface-2))', color: 'var(--color-text, var(--ds-text))', fontSize: 13.5, whiteSpace: 'pre-wrap' }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted, var(--ds-text-muted))', marginBottom: 3 }}>{eventLine(e, viewer)} · {when(e.created_at)}</div>
                {e.body}
              </div>
            ) : (
              <div style={{ fontSize: 11.5, color: 'var(--color-text-muted, var(--ds-text-muted))' }}>{eventLine(e, viewer)} · {when(e.created_at)}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export { when as fmtWhen };
